import { useEffect, useState, useMemo } from "react";
import { message } from 'antd'
import { useQuery, gql, useLazyQuery } from '@apollo/client';
import { useSelector } from 'react-redux';

const QUERY_NOMINATION_CONFIGURATIONS = gql`query QueryNominationConfigurations($companyId: ID!, $limit: Int, $nextToken: String) {
queryNominationConfigurationsByCompanyId(
    companyId: $companyId
    limit: $limit
    nextToken: $nextToken
  ) {
    items {
      awardedCount
      badge {
        assignmentLevelId
        assignmentLimit
        assignmentSSORequiredId
        badgeId
        automationRule
        badgeRotation
        badgeTypeId
        bonusPoints
        color
        companyId
        createdAt
        createdBy
        description
        gradientColor
        iconId
        id
        image
        isAssignmentSSORequired
        isPreApproved
        isRecipientSSORequired
        isRewardRequiresDate
        label
        physicalAwardActivityId
        name
        points
        recipientLevelId
        recipientPayTypeId
        recipientSSORequiredId
        rewardBonus
        rewardMaxBonus
        snappyCampaignTierId
        snappyGrantId
        updatedAt
        updatedBy
        visibility
        snappyGrant {
          costInCurrency
          id
          name
          description
          snappyCampaignId
          snappyCampaignTierId
        }
        snappyCampaignTier {
          id
          description
          name
          snappyCampaignId
          totalCost
          pointsRequired
        }
      }
      badgeId
      configId
      createdAt
      endDate
      id
      itemType
      nominationConfigurationId
      nominatorEmployeeLevelId
      nominatorEmployeePayTypeId
      nominatorEmployeelogInTypeId
      nomineeEmployeeLevelId
      nomineeEmployeePayTypeId
      nomineeEmployeelogInTypeId
      startDate
      status
      updatedAt
    }
    nextToken
  }
}`

const QUERY_NOMINATIONS = gql`query QueryNominationsByConfigId($configId: ID!, $companyId: ID!, $limit: Int, $nextToken: String) {
queryNominationsByConfigId(
    configId: $configId
    companyId: $companyId
    limit: $limit
    nextToken: $nextToken
  ) {
    items {
      companyId
      configId
      createdAt
      id
      isWinner
      itemType
      nominationConfigurationId
      nominationId
      nominationReason
      nominator {
        id
        firstName
        lastName
        title
        employeeId
        departmentId
      }
      nominatorId
      nominee {
        firstName
        id
        lastName
        title
        userGroupId
        subCompanyId
        employeeId
        departmentId
      }
      nomineeId
      reason
      updatedAt
    }
    nextToken
  }
}`

const useGetNominatedConfigList = () => {
    const [nominatedBadgeList, setNominatedBadgeList] = useState([])
    const [nominationsData, setNominationsData] = useState({})
    const currentUser = useSelector(state => state?.user?.currentUser);
    
    const { data, loading, error, refetch } = useQuery(QUERY_NOMINATION_CONFIGURATIONS, {
        variables: { companyId: currentUser?.companyId, limit: 1000 },
        skip: !currentUser?.companyId,
        fetchPolicy: 'cache-and-network'
    });

    const [getNominations] = useLazyQuery(QUERY_NOMINATIONS, {
        fetchPolicy: 'cache-and-network'
    });

    const processedRecords = useMemo(() => {
        if (!data?.queryNominationConfigurationsByCompanyId?.items) return [];

        return data.queryNominationConfigurationsByCompanyId.items.map(config => ({
            id: config.id,
            status: config.status,
            badge: {
                name: config.badge?.name || 'Unknown Badge'
            },
            startDate: config.startDate,
            endDate: config.endDate,
            awardedCount: config.awardedCount || 0,
            nomineeEmployeeLevelId: config.nomineeEmployeeLevelId || [],
            nomineeEmployeelogInTypeId: config.nomineeEmployeelogInTypeId,
            nomineeEmployeePayTypeId: config.nomineeEmployeePayTypeId,
            nominatorEmployeePayTypeId: config.nominatorEmployeePayTypeId,
            nominatorEmployeelogInTypeId: config.nominatorEmployeelogInTypeId,
            nominatorEmployeeLevelId: config.nominatorEmployeeLevelId || [],
            nominated: nominationsData[config.id]?.length || 0,
            winnerDetails: []
        }));
    }, [data, nominationsData]);

    useEffect(() => {
        if (!data?.queryNominationConfigurationsByCompanyId?.items || !currentUser?.companyId) return;

        const fetchAllNominations = async () => {
            const newNominationsData = {};
            
            for (const config of data.queryNominationConfigurationsByCompanyId.items) {
                try {
                    const { data: nominationsResult } = await getNominations({
                        variables: {
                            configId: config.id,
                            companyId: currentUser.companyId,
                            limit: 1000
                        }
                    });
                    
                    newNominationsData[config.id] = nominationsResult?.queryNominationsByConfigId?.items || [];
                } catch (error) {
                    console.error(`Error fetching nominations for config ${config.id}:`, error);
                    newNominationsData[config.id] = [];
                }
            }
            
            setNominationsData(newNominationsData);
        };

        fetchAllNominations();
    }, [data, currentUser?.companyId, getNominations]);

    useEffect(() => {
        if (processedRecords.length === 0) return;

        const nominationConfigurationOpenList = processedRecords.filter(({ status }) => status === "Open") || [];
        const nominationConfigurationCompleteList = processedRecords.filter(({ status }) => status === "Complete") || [];
        const nominationConfigurationPendingList = processedRecords.filter(({ status }) => status === "Pending") || [];
        const nominationConfigurationSubmittedList = processedRecords.filter(({ status }) => status === "Submitted") || [];
        
        setNominatedBadgeList([
            ...nominationConfigurationOpenList,
            ...nominationConfigurationPendingList,
            ...nominationConfigurationSubmittedList,
            ...nominationConfigurationCompleteList,
        ]);
    }, [processedRecords]);

    const getNominationConfiguration = async () => {
        try {
            await refetch();
        } catch (error) {
            message.error('Failed to fetch recognitions list! Try again later.');
        }
    };

    if (error) {
        message.error('Failed to fetch recognitions list! Try again later.');
    }

    return ({
        nominatedBadgeListLoader: loading,
        nominatedBadgeList,
        getNominationConfiguration,
    })
}

export default useGetNominatedConfigList